import { Component, ChangeDetectionStrategy, ViewEncapsulation, inject, ChangeDetectorRef, TemplateRef, effect, Injector, input, ElementRef, DestroyRef, output, signal, computed, contentChild, model, viewChild, viewChildren, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Command } from '@workbuddy/ui/model';
import { ButtonDisplayStyle, ButtonMode, ButtonSize, ButtonIconSize, ButtonMenuAlign } from './model';
import { ConnectedPosition, OverlayModule, } from '@angular/cdk/overlay';
import { Directive } from '@angular/core';

import * as Enumerable from 'linq';
import { Content, DynamicViewDirective } from '../overview';
import { SvgAvatarPipe } from '../avatar';

@Directive({
    selector: '[buttonOverlayContent]',
    standalone: true
})
export class ButtonOverlayContentDirective { }

@Directive({
    selector: '[tabButton]',
    standalone: true
})
export class TabButtonDirective {

    public elementRef = inject(ElementRef);
    public renderer = inject(Renderer2);

    public command = model.required<Command>({ alias: 'tabButton' });
    public visible = model(true, { alias: 'visible' });

    public get width() {
        return this.elementRef.nativeElement.offsetWidth;
    }

    constructor() {

        effect(() => {
            this.visible()
                ? this.renderer.setStyle(this.elementRef.nativeElement, 'visibility', 'visible')
                : this.renderer.setStyle(this.elementRef.nativeElement, 'visibility', 'hidden');
        });

    };

    public setVisibility(visible: boolean) {
        this.visible.set(visible);
    }
}

@Directive({
    selector: '[tabOverflowButton]',
    standalone: true
})
export class TabOverflowButtonDirective {

    public elementRef = inject(ElementRef);
    private renderer = inject(Renderer2);

    public visible = model(true, { alias: 'visible' });

    public get width() {
        return this.elementRef.nativeElement.offsetWidth;
    }

    constructor() {

        effect(() => {
            this.visible()
                ? this.renderer.setStyle(this.elementRef.nativeElement, 'visibility', 'visible')
                : this.renderer.setStyle(this.elementRef.nativeElement, 'visibility', 'hidden');
        });

    };

    public setVisibility(visible: boolean) {
        this.visible.set(visible);
    }
}

@Component({
    selector: 'w-button',
    templateUrl: `button.component.html`,
    styleUrls: [`button.component.less`],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        OverlayModule,
        DynamicViewDirective,
        TabButtonDirective,
        TabOverflowButtonDirective,
        SvgAvatarPipe
    ],
    host: {
        '[class]': "$class()",
    }
})
export class ButtonComponent {

    private injector = inject(Injector);
    private cdr = inject(ChangeDetectorRef);
    private host = inject(ElementRef);
    private destroyRef = inject(DestroyRef);

    public $command = model<Command>(null, { alias: 'command' });
    public $checked = model<boolean>(false, { alias: 'checked' });

    public $label = model<Content>(null, { alias: 'label' });
    public $disabled = model(false, { alias: 'disabled' });
    public $loading = model(false, { alias: 'loading' });
    public $hidden = model(false, { alias: 'hidden' });
    public $avatar = model<string>(null, { alias: 'avatar' });
    public $iconLeft = model<string>(null, { alias: 'iconLeft' });
    public $iconLeftColor = model<string>(null, { alias: 'iconLeftColor' });
    public $iconRight = model<string>(null, { alias: 'iconRight' });
    public $iconRightColor = model<string>(null, { alias: 'iconRightColor' });
    public $iconLeftSize = input(ButtonIconSize.Default, { alias: 'iconLeftSize' });
    public $iconRightSize = input(ButtonIconSize.Default, { alias: 'iconRightSize' });
    public $enableTabOverflow = input(false, { alias: 'enableTabOverflow' });

    public $count = model<number>(null, { alias: 'count' });
    public $tooltip = model<string>(null, { alias: 'tooltip' });
    public $data = model<any>(null, { alias: 'data' });

    public $commands = input<Command[]>(null, { alias: 'commands' });
    public $activeCommandId = input<string>(null, { alias: 'activeCommandId' });

    public $displayStyle = input(ButtonDisplayStyle.Secondary, { alias: 'displayStyle' });
    public $mode = model(ButtonMode.Button, { alias: 'mode' });
    public $size = input(ButtonSize.Small, { alias: 'size' });
    public $buttonClass = input<string>(null, { alias: 'buttonClass' });
    public $overlayClass = input<string>(null, { alias: 'overlayClass' });
    public $menuAlignment = input(ButtonMenuAlign.Right, { alias: 'menuAlignment' });
    public $menuItem = input(false, { alias: 'menuItem' });
    public $readonly = input(false, { alias: 'readonly' });
    public $moreCommands = input<Command[]>([], { alias: 'moreCommands' });

    public onClick = output<Event>();
    public onCheck = output<ButtonComponent>();
    public onCommandChanged = output<Command>();
    public menuOpen = output<boolean>();

    protected $overlayContent = contentChild(ButtonOverlayContentDirective, { read: TemplateRef });
    protected $buttonContainer = viewChild('buttonContainer', { read: ElementRef });
    protected $buttonTabs = viewChild('buttonTabs', { read: ElementRef });

    protected $tabOverflowButton = viewChild(TabOverflowButtonDirective);
    protected $tabButtons = viewChildren(TabButtonDirective);

    protected $overflowCommands = signal<Command[]>([]);
    protected $overflowActive = computed(() => {
        const overflowCommands = this.$overflowCommands();
        const activeCommand = this.$activeCommand();
        return Enumerable.from(overflowCommands).any?.(x => x.data === activeCommand);
    })

    protected $menuOpen = signal(false);
    protected $activeCommand = signal<Command>(null);

    protected $effectiveOverlayClass = computed(() => {

        const mode = this.$mode();
        const overlayClass = this.$overlayClass();

        const isMenu = [ButtonMode.Menu, ButtonMode.Dropdown, ButtonMode.Split].includes(mode);

        return overlayClass
            ? isMenu ? ['button-overlay', 'button-menu-commands', overlayClass] : ['button-overlay', overlayClass]
            : isMenu ? ['button-overlay', 'button-menu-commands'] : 'button-overlay';
    });

    protected $overlayPositions = computed<ConnectedPosition[]>(() => {
        const menuAlignment = this.$menuAlignment();

        return (menuAlignment ?? ButtonMenuAlign.Right) === ButtonMenuAlign.Right
            ? this.bottomRightAlign
            : this.bottomLeftAlign
    });

    protected $class = computed(() => {

        const buttonClass = this.$buttonClass();
        const checked = this.$checked();
        const readonly = this.$readonly();
        const menuOpen = this.$menuOpen();
        const size = this.$size();
        const mode = this.$mode();
        const displayStyle = this.$displayStyle();
        const leftIconSize = this.$iconLeftSize();
        const rightIconSize = this.$iconRightSize();

        const classes = {
            'btn-small': size === ButtonSize.Small,
            'btn-large': size === ButtonSize.Large,
            'btn-medium': size === ButtonSize.Medium,

            'btn-split': mode === ButtonMode.Split,
            'btn-menu': mode === ButtonMode.Menu,
            'btn-menu-item': mode === ButtonMode.MenuItem,
            'btn-dropdown': mode === ButtonMode.Dropdown,
            'btn-overlay': mode === ButtonMode.Overlay,
            'btn-toggle': mode === ButtonMode.Toggle,
            'btn-button': mode === ButtonMode.Button,
            'btn-select': mode === ButtonMode.Select,
            'btn-tab': mode === ButtonMode.Tab,
            'btn-icon': mode === ButtonMode.Icon,
            'btn-link': mode === ButtonMode.Link,

            'btn-secondary': displayStyle === ButtonDisplayStyle.Secondary,
            'btn-primary': displayStyle === ButtonDisplayStyle.Primary,
            'btn-tertiary': displayStyle === ButtonDisplayStyle.Tertiary,

            'btn-icon-left-small': leftIconSize === ButtonIconSize.Small,
            'btn-icon-left-medium': leftIconSize === ButtonIconSize.Medium,
            'btn-icon-left-large': leftIconSize === ButtonIconSize.Large,

            'btn-icon-right-small': rightIconSize === ButtonIconSize.Small,
            'btn-icon-right-medium': rightIconSize === ButtonIconSize.Medium,
            'btn-icon-right-large': rightIconSize === ButtonIconSize.Large,

            'btn-checked': checked,
            'btn-readonly': readonly,
            'menu-open': menuOpen,
        }

        const effectiveClasses = Enumerable
            .from(Object.keys(classes))
            .where(x => !!classes[x])
            .toArray();

        return effectiveClasses.length
            ? [buttonClass, ...effectiveClasses].join(' ')
            : buttonClass;

    });

    protected leftAlign: ConnectedPosition = {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
    };

    protected rightAlign: ConnectedPosition = {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
    };

    protected bottomLeftAlign = [
        {// top-left
            originX: 'start', // left corner of the button
            originY: 'bottom', // bottom corner of the button
            overlayX: 'start', // left corner of the overlay to the origin
            overlayY: 'top', // top corner of the overlay to the origin
        },
        {
            //     // bottom-left
            originX: 'start', // left corner of the button
            originY: 'top', // top corner of the button
            overlayX: 'start', // left corner of the overlay to the origin
            overlayY: 'bottom', // top corner of the overlay to the origin
        },
    ] as ConnectedPosition[];

    protected readonly bottomRightAlign = [
        {// top-left
            originX: 'end', // left corner of the button
            originY: 'bottom', // bottom corner of the button
            overlayX: 'end', // left corner of the overlay to the origin
            overlayY: 'top', // top corner of the overlay to the origin
        },
        {
            //     // bottom-left
            originX: 'end', // left corner of the button
            originY: 'top', // top corner of the button
            overlayX: 'end', // left corner of the overlay to the origin
            overlayY: 'bottom', // top corner of the overlay to the origin
        },
    ] as ConnectedPosition[];

    protected resizeObserver = new ResizeObserver(resize => {
        this.measureTabButtons();
    });

    protected ButtonDisplayStyle = ButtonDisplayStyle;
    protected ButtonMode = ButtonMode;
    protected ButtonSize = ButtonSize;
    protected ButtonIconSize = ButtonIconSize;


    constructor() {

        effect(() => {
            const activeCommandId = this.$activeCommandId();

            if (!activeCommandId) return;

            const commands = this.$commands();
            if (!commands?.length) return;

            const command = Enumerable.from(commands).firstOrDefault(x => x._id === activeCommandId);

            if (!command) return;
            this.onSelectButtonClick(command);

        });

        effect(() => {

            const mode = this.$mode();
            if (![ButtonMode.Dropdown, ButtonMode.Tab, ButtonMode.Select].includes(mode)) return;

            const commands = this.$commands();
            if (!commands?.length) return;

            const activeCommand = this.$activeCommand();
            if (commands?.includes(activeCommand)) return;

            if (mode === ButtonMode.Dropdown) {
                let command = Enumerable.from(commands).firstOrDefault(x => x.isDefault);
                if (command) {
                    this.onSelectMenu(command);
                }

            } else if ([ButtonMode.Tab, ButtonMode.Select].includes(mode)) {
                let command = Enumerable.from(commands).firstOrDefault(x => x.isDefault) ?? Enumerable.from(commands).firstOrDefault();
                if (command) {
                    this.onSelectButtonClick(command);
                }
            }

        });

        effect(() => {

            const command = this.$command();
            if (!command) return;

            const changed = command.$changed();
            this.refreshCommand();

        });

        effect(() => {
            this.resizeObserver.disconnect();

            const mode = this.$mode();
            if (![ButtonMode.Tab].includes(mode)) return;

            const enableTabOverflow = this.$enableTabOverflow();
            if (!enableTabOverflow) return;

            const buttonContainer = this.$buttonContainer();
            if (!buttonContainer) return;

            this.resizeObserver.observe(buttonContainer.nativeElement);
        });

        effect(() => {
            const buttons = this.$tabButtons();
            setTimeout(() => this.measureTabButtons());
        });

        effect(() => {
            const activeCommand = this.$activeCommand();
            this.$overflowCommands.set(this.buildOverflowCommands());
        });

        this.destroyRef.onDestroy(() => {
            this.resizeObserver.disconnect();
        });

        effect(() => {
            const menuOpen = this.$menuOpen();
            this.menuOpen.emit(menuOpen);
        });
    }

    protected toggleButton = (event: Event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        this.$menuOpen.update(x => !x);
    }

    public hideMenu() {
        this.$menuOpen.set(false);
    }

    public showMenu() {
        this.$menuOpen.set(true);
    }

    protected onButtonClick(event: Event) {

        // If the button is disabled, prevent the click event
        if (this.$disabled()) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const command = this.$command();

        if (command?.action) {
            command?.action();
        }

        this.onClick.emit(event);
        this.cdr.markForCheck();
    }

    protected onMenuButtonClick(command: Command) {

        this.hideMenu();

        if (command) command?.action();
    }

    protected onMenuItemClick(command: Command) {

        const data = this.$data();

        this.hideMenu();

        if (command?.action) command?.action(data);
        this.onCommandChanged.emit(command);
    }

    protected onSelectButtonClick(command: Command) {

        const activeCommand = this.$activeCommand();

        if (activeCommand === command) return;

        this.$activeCommand.set(command);
        if (command?.action) command?.action();

    }

    protected onToggleClick(event: Event) {

        this.$checked.update(x => !x);
        this.onCheck.emit(this);
    }

    protected onSelectMenu(command: Command) {

        this.$command.set(command);
        this.hideMenu();

        this.onCommandChanged.emit(command);
    }

    private measureTabButtons() {

        const enableTabOverflow = this.$enableTabOverflow();
        if (!enableTabOverflow) return;

        const buttonContainer = this.$buttonTabs();
        const tabButtons = this.$tabButtons();
        const tabOverflowButton = this.$tabOverflowButton();

        if (!tabButtons?.length) return;

        let stopWidth = 0;

        const containerWidth = buttonContainer.nativeElement.offsetWidth;
        const buttonWidth = Enumerable.from(tabButtons).sum(x => x.width);

        const overflowWidth = tabOverflowButton.width + 3;
        const hasOverflow = buttonWidth > containerWidth;

        const safeContainerWidth = hasOverflow
            ? containerWidth - overflowWidth
            : containerWidth;


        tabButtons
            .forEach((button, index) => {
                stopWidth += button.width + 3;
                button.setVisibility((safeContainerWidth > stopWidth));
            });

        const overflowCommands = this.buildOverflowCommands();
        const visibleCommands = tabButtons.filter(x => x.visible()).map(x => x.command());

        this.$overflowCommands.set(overflowCommands);
        tabOverflowButton.setVisibility(overflowCommands.length > 0);

        // console.log('visible', visibleCommands?.length, 'overflow', overflowCommands?.length);
    }

    private buildOverflowCommands() {
        const tabButtons = this.$tabButtons();
        const overflowCommands = tabButtons
            .filter(x => !x.visible())
            .map(x => {
                const originalCommand = x.command();
                const activeCommand = this.$activeCommand();

                return new Command({
                    label: originalCommand.label,
                    iconLeft: activeCommand === originalCommand ? 'fa-check' : 'fa-fw',
                    action: () => this.onSelectButtonClick(originalCommand),
                    data: originalCommand,
                });
            });
        return overflowCommands;
    }

    private refreshCommand() {
        const command = this.$command();
        if (!command) return;

        this.$label.set(command?.label);
        this.$iconLeft.set(command?.iconLeft);
        this.$iconRight.set(command?.iconRight);
        this.$tooltip.set(command?.tooltip);
        this.$count.set(command?.count);
        this.$disabled.set(command?.disabled);
        this.$hidden.set(command?.hidden);
        this.$data.set(command?.data);
        this.$iconLeftColor.set(command?.iconLeftColor);
        this.$iconRightColor.set(command?.iconRightColor);

        this.cdr.markForCheck();
    }


}

export const BUTTON_COMPONENTS = [
    ButtonComponent,
    ButtonOverlayContentDirective,
];